import { useQuery } from "@tanstack/react-query";
import {
  buildListParams,
  type DataGridQuery,
  DEFAULT_LIMIT,
  type PageRequest,
} from "@/hooks/use-data-grid";
import { engineError } from "@/lib/engine-error";
import type { AccountRow, ListEnvelope } from "@/lib/profitability-api";
import { queryKeys } from "@/lib/query";

export function useAccounts(
  query: DataGridQuery,
  page: PageRequest,
  range?: string,
  contractType?: string,
  clientPartner?: string[],
  limit: number = DEFAULT_LIMIT,
) {
  // Joined for the query KEY only -- it just has to change when the selection
  // does. The request below sends one param per name.
  const partnerKey = (clientPartner ?? []).join("|");
  return useQuery({
    queryKey: [
      ...queryKeys.accounts(query, page),
      range ?? "default",
      contractType ?? "all",
      partnerKey || "any",
      limit,
    ],
    queryFn: async (): Promise<ListEnvelope<AccountRow>> => {
      const params = buildListParams(query, page, limit);
      if (range) params.set("range", range);
      // "all" is the default and stays out of the request entirely.
      if (contractType && contractType !== "all") params.set("contract_type", contractType);
      // Server-side, unlike the other filters on this screen. The list pages at
      // 50 of 116, so filtering in the browser would offer only the partners on
      // the current page and narrow that page alone -- "show me my accounts"
      // has to mean all of them, not the ones that happen to be visible.
      //
      // One param PER NAME, never a comma-joined string. The endpoint reads
      // every `client_partner` param it is given, but it also splits each one
      // on commas -- so a partner whose name contains a comma ("Smith, John")
      // would silently become two filters that match nobody. Repeated params
      // are the only form that survives a name like that.
      for (const partner of clientPartner ?? []) params.append("client_partner", partner);
      const response = await fetch(`/api/accounts?${params.toString()}`);
      if (!response.ok) {
        throw await engineError(response, "Could not load accounts");
      }
      return response.json();
    },
    // Month-end close data. Re-fetching on every window focus would hammer the
    // engine for numbers that change once a month.
    staleTime: 5 * 60 * 1000,
  });
}
